// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ExternalLink,
  Search,
} from "lucide-react";
import { Fragment, memo, useMemo, useState } from "react";
import { CSVExportButton } from "@/components/csv-export-button";
import { ExportFilenameDialog } from "@/components/export-filename-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  type Opportunity,
  useAccountOpportunities,
} from "@/hooks/api/use-account-opportunities";
import { useAccountsByIds } from "@/hooks/api/use-accounts";
import type { ExternalConnectionLinks } from "@/hooks/api/use-external-connection-links";
import { useTableCSVExport } from "@/hooks/use-csv-export";
import { getStageVariant } from "@/lib/badge-utils";
import { formatDate } from "@/lib/date-formatting";
import {
  getExternalConnectionLink,
  getExternalSystemName,
} from "@/lib/external-connection-links";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import { formatRelationSource } from "@/utils/formatters";

type SortField = "name" | "stage" | "type" | "created" | "close";

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

interface DateCellProps {
  date: string | null;
  label: string;
  onDateClick?: (date: string) => void;
}

const DateCell = memo(function DateCell({ date, onDateClick }: DateCellProps) {
  if (!date) return <span>-</span>;

  return (
    <div className="flex items-center">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => onDateClick?.(date)}
            className="group/date gap-2 flex items-center hover:text-muted-foreground hover:bg-muted rounded px-2 py-1 -mx-2 -my-1 transition-colors"
            tabIndex={-1}
          >
            <span>{formatDate(date)}</span>
            <Search className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors opacity-0 group-hover/date:opacity-100" />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Jump to this date in account story</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
});

interface OpportunityContactsTableProps {
  opportunity: Opportunity;
}

function OpportunityContactsTable({
  opportunity,
}: OpportunityContactsTableProps) {
  // Ensure contacts is always an array, even if it's undefined
  const contacts = opportunity.contacts || [];

  return (
    <div className="px-8 py-2">
      <div className="border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-border bg-muted/50">
              <TableHead className="h-9 text-xs text-left w-[160px]">
                Name
              </TableHead>
              <TableHead className="h-9 text-xs">Email</TableHead>
              <TableHead className="h-9 text-xs">Title</TableHead>
              <TableHead className="h-9 text-xs">Type</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="h-12 text-center text-muted-foreground"
                >
                  No contacts found for this opportunity
                </TableCell>
              </TableRow>
            ) : (
              contacts.map((contact) => (
                <TableRow
                  key={contact.id}
                  className="hover:bg-muted/50 border-b border-border/50 last:border-0"
                >
                  <TableCell className="py-2">
                    <span className="text-sm text-muted-foreground">
                      {contact.first_name} {contact.last_name}
                    </span>
                  </TableCell>
                  <TableCell className="py-2">
                    {contact.email_address && (
                      <a
                        href={`mailto:${contact.email_address}`}
                        className="text-sm text-primary"
                      >
                        {contact.email_address}
                      </a>
                    )}
                  </TableCell>
                  <TableCell className="py-2">
                    <span className="text-sm text-muted-foreground">
                      {contact.job_title}
                    </span>
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex flex-wrap gap-1">
                      {contact.relation_sources.map((source, index) => (
                        <Badge
                          key={index}
                          variant={
                            source === "activity_based_healing"
                              ? "secondary"
                              : "default"
                          }
                        >
                          {formatRelationSource(source)}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// Define export columns for opportunities
const opportunityExportColumns: UnifiedTableColumn<Opportunity>[] = [
  {
    key: "name",
    title: "Name",
  },
  {
    key: "stage_name",
    title: "Stage",
  },
  {
    key: "type",
    title: "Type",
  },
  {
    key: "created_date",
    title: "Created Date",
  },
  {
    key: "close_date",
    title: "Close Date",
  },
  {
    key: "is_closed",
    title: "Is Closed",
    exportValue: (item: Opportunity) => (item.is_closed ? "Yes" : "No"),
  },
  {
    key: "is_won",
    title: "Is Won",
    exportValue: (item: Opportunity) => (item.is_won ? "Yes" : "No"),
  },
  {
    key: "source_record_id",
    title: "Source Opportunity ID",
  },
];

interface AccountOpportunitiesProps {
  accountId?: string;
  onDateClick?: (date: string) => void;
  connections?: ExternalConnectionLinks[];
  onOpportunitySelect?: (opportunityId: string) => void;
}

export function AccountOpportunities({
  accountId,
  onDateClick,
  connections,
  onOpportunitySelect,
}: AccountOpportunitiesProps) {
  const { data: opportunities, isLoading } = useAccountOpportunities(accountId);
  const { data: accounts } = useAccountsByIds(
    accountId ? [accountId] : undefined,
  );
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: "created",
    direction: "desc",
  });
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const hasSalesforceOpportunities =
    opportunities?.[0]?.source_record_type === "salesforce.opportunity";

  const sortedOpportunities = useMemo(() => {
    if (!opportunities) return [];

    return [...opportunities].sort((a, b) => {
      const { field, direction } = sortConfig;
      const multiplier = direction === "asc" ? 1 : -1;

      switch (field) {
        case "name":
          return multiplier * a.name.localeCompare(b.name);
        case "stage":
          return multiplier * a.stage_name.localeCompare(b.stage_name);
        case "type":
          return multiplier * (a.type || "").localeCompare(b.type || "");
        case "created":
          return (
            multiplier *
            (new Date(a.created_date).getTime() -
              new Date(b.created_date).getTime())
          );
        case "close":
          if (!a.close_date && !b.close_date) return 0;
          if (!a.close_date) return multiplier;
          if (!b.close_date) return -multiplier;
          return (
            multiplier *
            (new Date(a.close_date).getTime() -
              new Date(b.close_date).getTime())
          );
        default:
          return 0;
      }
    });
  }, [opportunities, sortConfig]);

  // Set up CSV export
  const { isExporting, exportData, defaultFilename } = useTableCSVExport({
    data: sortedOpportunities,
    columns: opportunityExportColumns,
    filename: `${
      accounts?.results?.find((a) => a.id === accountId)?.name ||
      `account-${accountId}`
    }_opportunities_${new Date().toISOString().split("T")[0]}`,
  });

  // State for export filename dialog
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const handleExport = (filename: string) => {
    exportData(filename);
  };

  const toggleSort = (field: SortField) => {
    setSortConfig((current) => ({
      field,
      direction:
        current.field === field
          ? current.direction === "asc"
            ? "desc"
            : "asc"
          : "desc",
    }));
  };

  const toggleRow = (opportunityId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [opportunityId]: !prev[opportunityId],
    }));
  };

  const SortableHeader = ({
    field,
    children,
  }: {
    field: SortField;
    children: React.ReactNode;
  }) => (
    <Button
      variant="ghost"
      className="h-8 px-2 hover:bg-muted flex items-center gap-2"
      onClick={() => toggleSort(field)}
    >
      {children}
      {sortConfig.field === field &&
        (sortConfig.direction === "desc" ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronUp className="h-4 w-4" />
        ))}
    </Button>
  );

  if (!accountId) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold">Opportunities</h2>

        {/* CSV Export Button */}
        {!isLoading && sortedOpportunities.length > 0 && (
          <ExportFilenameDialog
            open={isExportDialogOpen}
            onOpenChange={setIsExportDialogOpen}
            defaultFilename={defaultFilename}
            extension="csv"
            onExport={handleExport}
            disabled={isExporting}
          >
            <CSVExportButton
              onClick={() => setIsExportDialogOpen(true)}
              isExporting={isExporting}
              disabled={isExporting}
              tooltipText="Export opportunities to CSV"
            />
          </ExportFilenameDialog>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="w-8"></TableHead>
            <TableHead>
              <SortableHeader field="name">Name</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="stage">Stage</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="type">Type</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="created">Created</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="close">Close date</SortableHeader>
            </TableHead>
            {hasSalesforceOpportunities && (
              <TableHead className="w-8">CRM</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={hasSalesforceOpportunities ? 7 : 6}>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </TableCell>
            </TableRow>
          ) : sortedOpportunities?.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={hasSalesforceOpportunities ? 7 : 6}
                className="h-24 text-center text-muted-foreground"
              >
                No opportunities found
              </TableCell>
            </TableRow>
          ) : (
            sortedOpportunities?.map((opportunity) => (
              <Fragment key={opportunity.id}>
                <TableRow className="group">
                  <TableCell className="w-8">
                    <button
                      onClick={() => toggleRow(opportunity.id)}
                      className="p-1 hover:bg-accent rounded-sm"
                    >
                      {expandedRows[opportunity.id] ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center group/name">
                      {onOpportunitySelect ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() =>
                                onOpportunitySelect(opportunity.id)
                              }
                              className="flex items-center hover:text-muted-foreground hover:bg-muted rounded px-2 py-1 -mx-2 -my-1 transition-colors"
                              tabIndex={-1}
                            >
                              <span>{opportunity.name}</span>
                              <Search className="ml-2 h-4 w-4 text-muted-foreground transition-colors opacity-0 group-hover/name:opacity-100" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              Select this buying group in Account Story Viewer
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <span>{opportunity.name}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={getStageVariant(
                        opportunity.is_closed,
                        opportunity.is_won,
                      )}
                    >
                      {opportunity.stage_name}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="opportunity-type">{opportunity.type}</Badge>
                  </TableCell>
                  <TableCell>
                    <DateCell
                      date={opportunity.created_date}
                      label="creation date"
                      onDateClick={onDateClick}
                    />
                  </TableCell>
                  <TableCell>
                    <DateCell
                      date={opportunity.close_date}
                      label="close date"
                      onDateClick={onDateClick}
                    />
                  </TableCell>
                  {hasSalesforceOpportunities && (
                    <TableCell>
                      {(() => {
                        const url = getExternalConnectionLink(
                          opportunity,
                          connections,
                        );
                        if (!url) return null;

                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center w-8 h-8 text-muted-foreground hover:text-foreground transition-colors"
                                tabIndex={-1}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                View in{" "}
                                {getExternalSystemName(
                                  opportunity.source_record_type,
                                )}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                  )}
                </TableRow>
                {expandedRows[opportunity.id] && (
                  <TableRow>
                    <TableCell
                      colSpan={hasSalesforceOpportunities ? 7 : 6}
                      className="p-0 hover:bg-background"
                    >
                      <OpportunityContactsTable opportunity={opportunity} />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
